import React, { useCallback, useContext, useState } from 'react';
import axios from 'axios';
import { useSnackbar } from 'src/utils/Hooks';
import { ExportContext } from 'src/providers/ExportContextProvider';
import { GenericErrorMessage } from 'src/components/BeaconV2Components/common/CommonSnackbarMessages/GenericErrorMessage';
import { useDorisApplyEntityFilters } from 'src/utils/Hooks/useDorisApplyEntityFilters';

// Reusable hook to submit Doris export requests in a consistent way
// Encapsulates: applying entity filters, posting to endpoint, snackbar UX, polling, and closing modal
export type DorisExportBaseParams = {
  exportType?: string;
  fieldName?: string;
  metricType?: string;
  shouldGroupByWeek?: boolean;
  retailerId: number;
  startWeekId: number;
  endWeekId: number;
  fileName: string;
  comparisonStartWeekId?: number;
  comparisonEndWeekId?: number;
};

export interface UseDorisExportActionArgs {
  endpoint: string;
  params: DorisExportBaseParams;
  onClose: () => void;
}

export const useDorisExportAction = ({ endpoint, params, onClose }: UseDorisExportActionArgs) => {
  const applyEntityFilters = useDorisApplyEntityFilters();
  const { showSnackbar } = useSnackbar();
  const { startPolling } = useContext(ExportContext);
  const [isExporting, setIsExporting] = useState(false);

  const handleExportClick = useCallback(async () => {
    try {
      setIsExporting(true);

      const exportParams = applyEntityFilters({
        ...params
      });

      await axios.post(`${endpoint}`, exportParams);
      showSnackbar({ type: 'success', message: 'Your download is processing.' });
      startPolling();
      onClose();
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('Export failed:', error);
      showSnackbar({
        message: <GenericErrorMessage />,
        type: 'error'
      });
    } finally {
      setIsExporting(false);
    }
  }, [applyEntityFilters, endpoint, onClose, params, showSnackbar, startPolling]);

  return { handleExportClick, isExporting };
};
